/**
 * Admin export: sends countries.json to the Worker, which commits it to GitHub.
 * The GitHub token lives only in the Worker (GITHUB_TOKEN secret); the browser
 * authenticates with the short-lived session token issued by /api/admin/login.
 * Cloudflare auto-deploys from the commit, so changes go live in about a minute.
 */
import type { CountryData } from '../types';
import { getAdminToken, clearAdminSession } from './state';

const SESSION_EXPIRED = 'Admin session expired. Reload the page and enter the password again.';

export async function saveCountriesToServer(countries: CountryData[]): Promise<{ ok: boolean; message: string }> {
  const token = getAdminToken();
  if (!token) return { ok: false, message: SESSION_EXPIRED };

  try {
    const res = await fetch('/api/admin/save-countries', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(countries),
    });
    const data = await res.json().catch(() => ({})) as { ok?: boolean; message?: string; error?: string };

    if (res.status === 401) {
      clearAdminSession();
      return { ok: false, message: SESSION_EXPIRED };
    }
    if (!res.ok) return { ok: false, message: data.error || `Save failed: ${res.status}` };

    return { ok: true, message: data.message || 'Saved to GitHub, live in about a minute' };
  } catch (err) {
    return { ok: false, message: (err as Error).message };
  }
}
